import { useTranslation } from 'react-i18next';
import type { KeyboardEvent } from 'react';

import {
  FormatCurrencyForDisplay,
  Input,
  SkeletonBase,
} from '@unified-checkout/design-system';

import type { IInstantSettlementAmountProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/instant-settlement';
import {
  BALANCE_FORM_FIELDS,
  INSTANT_SETTLEMENT_BALANCE_TYPES,
} from '@app/dashboard/constants/balance_and_transfers/balance/modals';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';
import { useLangStore } from '@app/dashboard/store/lang';

import styles from './amount-input.module.scss';

const AmountInput = ({
  isFeesOrSubmitLoading,
  isGetBalanceTypesLoading,
  fields,
  handleFieldChange,
  selectedBalance,
  selectedBalanceType,
}: IInstantSettlementAmountProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const amountField = fields[BALANCE_FORM_FIELDS.AMOUNT];

  const amountWithCurrency = formatAmountWithCurrency({
    amount: selectedBalance?.balance || 0,
    currency: selectedBalance?.currency || COUNTRIES.EGY,
    lang,
  });

  const preventExponentialNotationInput = (
    e: KeyboardEvent<HTMLInputElement>
  ) => {
    if (['E', 'e'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const amountInputHintText = !amountField?.errorMsg && (
    <>
      {t('innerLayout.balanceTransfers.balance.instantSettlement.maxAmount', {
        amount: amountWithCurrency,
      })}
      {FormatCurrencyForDisplay(amountWithCurrency)}
    </>
  );

  return isGetBalanceTypesLoading ? (
    <SkeletonBase width="100%" height="5.625rem" />
  ) : (
    <div
      className={styles['instant-settlement__amount-input']}
      data-amount-default-case={!amountField?.errorMsg}
    >
      {!selectedBalance?.allow_partial_settlement ? (
        <p
          className={styles['instant-settlement__amount-input-full-settlement']}
        >
          {t(
            `innerLayout.balanceTransfers.balance.instantSettlement.${
              selectedBalanceType === INSTANT_SETTLEMENT_BALANCE_TYPES.PENDING
                ? 'alternativePaymentsFullSettlementHint'
                : 'cardWalletFullSettlementHint'
            }`
          )}
        </p>
      ) : (
        <Input
          type="number"
          name={BALANCE_FORM_FIELDS.AMOUNT}
          label={t(
            'innerLayout.balanceTransfers.balance.instantSettlement.amount'
          )}
          value={amountField?.value || ''}
          status={!!amountField?.errorMsg ? 'error' : 'default'}
          placeholder={t(
            'innerLayout.balanceTransfers.balance.instantSettlement.enterAmount'
          )}
          error={amountField?.errorMsg}
          onChange={(e) => handleFieldChange(e, BALANCE_FORM_FIELDS.AMOUNT)}
          disabled={isFeesOrSubmitLoading}
          leadingText={
            FormatCurrencyForDisplay(
              selectedBalance?.currency || COUNTRIES.EGY
            ) as string
          }
          hintText={amountInputHintText as any}
          onKeyDown={preventExponentialNotationInput}
        />
      )}
    </div>
  );
};

export default AmountInput;
